import moment from 'moment-timezone';
import CheckCircleIcon from '@raydiant/raydial/components/Icons/CheckCircle';
import cn from 'classnames';
import Button from '@raydiant/raydial/components/Button';
import ErrorIcon from '@material-ui/icons/Error';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import PaperModalV2 from 'raydiant-elements/core/PaperModalV2';
import TextArea from '@raydiant/raydial/components/TextArea';
import WifiIcon from '@raydiant/raydial/components/Icons/Wifi';
import TimeField from 'raydiant-elements/core/TimeField';
import Heading2 from 'raydiant-elements/typography/Heading2';
import Link from '@raydiant/raydial/components/Link';
import InputHelperText from 'raydiant-elements/core/InputHelperText';
import Heading from 'raydiant-elements/core/Heading';
import EthernetIcon from 'raydiant-elements/icons/Ethernet';
import LTEIcon from 'raydiant-elements/icons/LTE';
import NetworkIcon from '@raydiant/raydial/components/Icons/Network';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import Column from 'raydiant-elements/layout/Column';
import Scrollable from 'raydiant-elements/layout/Scrollable';
import Text from '@raydiant/raydial/components/Text';
import { makeStyles, createStyles } from 'raydiant-elements/styles';
import { Theme } from 'raydiant-elements/theme';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as D from '../../clients/mira/types/Device';
import miraClient from '../../clients/miraClient';
import * as deviceActions from '../../actions/devices';
import * as soundZoneActions from '../../actions/soundZones';
import * as pairingCodeActions from '../../actions/pairingCodes';
import {
  selectSoundZones,
  selectSoundZonesFetching,
} from '../../selectors/soundZones';
import {
  selectPairingCodes,
  selectPairingCodesGenerating,
} from '../../selectors/pairingCodes';
import {
  selectIsEnterpriseUser,
  selectIsLiteUser,
  selectUserProfile,
} from '../../selectors/user';
import { selectPlaylistsById } from '../../selectors/v2/playlists';
import { selectDeviceErrorsById } from '../../selectors/v2/devices';
import {
  getDeviceConnectionStatus,
  canShareResource,
  getDeviceType,
  is8kResolutionSupported,
  is4kResolutionSupported,
  isDeviceSecondScreen,
  isDeviceSurfaceGo,
  isDevicePoly,
  canDeleteLocationDevice,
} from '../../utilities';
import Popover from '../../components/Popover';
import PropertiesTable from '../../components/PropertiesTable';
import UnregisterDeviceConfirmation from '../../components/UnregisterDeviceConfirmation';
import DeviceStatus from './DeviceStatus';
import DeviceSharing from './DeviceSharing';
import DeviceTags from './DeviceTags';
import getDeviceContentWarning from './getDeviceContentWarning';
import {
  deviceManagementRows,
  isAmazonSignageStick,
  propertyRows,
  showsConnectivity,
} from './deviceManagementRows';
import Row from 'raydiant-elements/layout/Row/Row';
import useCurrentUserRoles from '../../hooks/useCurrentUserRoles';
import DeviceIntegrationManager from '../../components/DeviceIntegrationManager';
import { useTranslation } from 'react-i18next';
import QuestionMarkIcon from '../../components/QuestionMarkIcon';
import TimezoneSelect from './components/TimezoneSelect';
import Toggle from '@raydiant/raydial/components/Toggle';
import config from '../../config';

const { supportAppUrl, SYBSoundZonePollMS } = config;

const fromTimeFieldValue = (time: string | null) => {
  if (!time) return '';

  return moment(time, 'HH:mm').format('HH:mm:ss');
};

const toTimeFieldValue = (time: string | null) => {
  if (!time) return '';

  return moment(time, 'HH:mm:ss').format('HH:mm');
};

const maximumDeviceUpdateBlackoutRange = 21;

interface DeviceSettingsProps {
  device: D.Device;
  lastLoadedDate: string;
  onClose: () => void;
  isVIOnly?: boolean;
}

const DeviceSettings = ({
  device: initialDevice,
  lastLoadedDate,
  onClose,
  isVIOnly = false,
}: DeviceSettingsProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [device, setDevice] = useState(initialDevice); //initialize device props to address race condition when creating new tags
  const roles = useCurrentUserRoles();

  // Selectors

  const soundZones = useSelector(selectSoundZones);
  const soundZonesFetching = useSelector(selectSoundZonesFetching);
  const pairingCodes = useSelector(selectPairingCodes);
  const pairingCodesGenerating = useSelector(selectPairingCodesGenerating);
  const currentUser = useSelector(selectUserProfile);
  const isEnterpriseUser = useSelector(selectIsEnterpriseUser);
  const playlistsById = useSelector(selectPlaylistsById);
  const deviceErrorsById = useSelector(selectDeviceErrorsById);
  const isLiteUser = useSelector(selectIsLiteUser);

  const deviceSoundZone = soundZones.find((sz) => sz.deviceId === device.id);
  const isFetchingSoundZone = soundZonesFetching.includes(device.id);
  const devicePairingCode = pairingCodes.find((c) => c.id === device.id);
  const isGeneratingPairingCode = pairingCodesGenerating.includes(device.id);
  const playlist = playlistsById[device.playlistId || ''] ?? null;
  const deviceErrors = deviceErrorsById[device.id] || [];

  const contentWarning =
    playlist && currentUser
      ? getDeviceContentWarning(device, playlist, currentUser)
      : null;

  // State
  const [updatedNotes, setNotes] = useState<string | null>(null);
  const [updatedResolution, setResolution] = useState<string | null>(null);
  const [updatedTimezone, setTimezone] = useState<string | null>(null);
  const [updatedOrientation, setOrientation] = useState<string | null>(null);
  const [updatedMaintenanceWindowEnabled, setMaintenanceWindowEnabled] =
    useState<boolean | null>(null);
  const [activeHoursStart, setActiveHoursStart] = useState<string>('');
  const [activeHoursEnd, setActiveHoursEnd] = useState<string>('');
  const [maintenanceWindowValid, setMaintenanceWindowValid] =
    useState<boolean>(true);
  const [dailyRebootTime, setDailyRebootTime] = useState<string>('');
  const [dailyRestartTime, setDailyRestartTime] = useState<string>('');
  const [musicServiceStatus, setMusicServiceStatus] = useState<boolean>(
    device?.services?.includes('music'),
  );
  const [restartStatus, setRestartStatus] = useState<
    '' | 'pending' | 'success' | 'error'
  >('');
  const [rebootStatus, setRebootStatus] = useState<
    '' | 'pending' | 'success' | 'error'
  >('');
  const [clearCacheStatus, setClearCacheStatus] = useState<
    '' | 'pending' | 'success' | 'error'
  >('');
  const [isUnregisterConfirmationOpen, setIsUnregisterConfirmationOpen] =
    useState(false);
  const [unregisterStatus, setUnregisterStatus] = useState<
    '' | 'pending' | 'success' | 'error'
  >('');
  const [rebootDeviceConfirmationOpen, setRebootDeviceConfirmationOpen] =
    useState<boolean>(false);
  const [rebootDeviceButtonEl, setRebootDeviceButtonEl] =
    useState<Element | null>(null);

  // Callbacks

  const updateDevice = useCallback(
    (updatedDevice: Partial<D.Device>) => {
      if (!device) return;
      dispatch(
        deviceActions.updateDevice(
          {
            ...device,
            ...updatedDevice,
          },
          {},
        ),
      );
      setDevice({ ...device, ...updatedDevice });
    },
    [dispatch, device],
  );

  const updateNotes = useCallback(() => {
    if (!device) return;
    if (updatedNotes === null) return;
    if (updatedNotes === device.name) return;
    updateDevice({ description: updatedNotes });
  }, [updateDevice, updatedNotes, device]);

  const updateDailyRebootTime = useCallback(() => {
    if (!device) return;
    if (dailyRebootTime === null) return;
    if (fromTimeFieldValue(dailyRebootTime) === device.dailyRebootTime) {
      return;
    }

    if (dailyRebootTime === '') {
      updateDevice({ dailyRebootTime: null });
    } else {
      updateDevice({
        dailyRebootTime: fromTimeFieldValue(dailyRebootTime),
      });
    }
  }, [updateDevice, dailyRebootTime, device]);

  const validate = useCallback(() => {
    let isValid = true;
    setMaintenanceWindowValid(isValid);

    // skip validation check if either timefield value is empty
    if (!activeHoursStart || !activeHoursEnd) {
      return;
    }

    const startTime = moment(activeHoursStart, 'HH:mm:ss');
    const endTime = moment(activeHoursEnd, 'HH:mm:ss');

    let duration = moment.duration(endTime.diff(startTime));
    // if start date is greater than end date, add a day to the end date and re-calculate difference
    if (duration.hours() < 0) {
      endTime.add(1, 'day');
      duration = moment.duration(endTime.diff(startTime));
    }

    if (
      duration.hours() > maximumDeviceUpdateBlackoutRange ||
      (duration.hours() === maximumDeviceUpdateBlackoutRange &&
        duration.minutes() > 0)
    ) {
      isValid = false;
    }

    setMaintenanceWindowValid(isValid);
    return isValid;
  }, [activeHoursStart, activeHoursEnd]);

  const updateActiveHoursStartTime = useCallback(() => {
    if (!device) return;
    if (!validate()) return;

    if (
      fromTimeFieldValue(activeHoursStart) === device.activeHoursStart &&
      fromTimeFieldValue(activeHoursEnd) === device.activeHoursEnd
    ) {
      return;
    }

    updateDevice({
      activeHoursStart: fromTimeFieldValue(activeHoursStart),
      activeHoursEnd: fromTimeFieldValue(activeHoursEnd),
    });
  }, [updateDevice, activeHoursStart, activeHoursEnd, device, validate]);

  const updateActiveHoursEndTime = useCallback(() => {
    if (!device) return;
    if (!validate()) return;

    if (
      fromTimeFieldValue(activeHoursStart) === device.activeHoursStart &&
      fromTimeFieldValue(activeHoursEnd) === device.activeHoursEnd
    ) {
      return;
    }

    updateDevice({
      activeHoursStart: fromTimeFieldValue(activeHoursStart),
      activeHoursEnd: fromTimeFieldValue(activeHoursEnd),
    });
  }, [updateDevice, activeHoursEnd, activeHoursStart, device, validate]);

  const updateDailyRestartTime = useCallback(() => {
    if (!device) return;
    if (dailyRestartTime === null) return;
    if (fromTimeFieldValue(dailyRestartTime) === device.dailyRestartTime) {
      return;
    }

    if (dailyRestartTime === '') {
      updateDevice({ dailyRestartTime: null });
    } else {
      updateDevice({
        dailyRestartTime: fromTimeFieldValue(dailyRestartTime),
      });
    }
  }, [updateDevice, dailyRestartTime, device]);

  const restartDevice = useCallback(async () => {
    setRestartStatus('pending');

    try {
      await miraClient.restartDevice(device.id);
      setRestartStatus('success');
    } catch (err) {
      setRestartStatus('error');
    }
  }, [device]);

  const openRebootDeviceConfirmation = useCallback((event) => {
    setRebootDeviceButtonEl(event.currentTarget);
    setRebootDeviceConfirmationOpen(true);
  }, []);

  const closeRebootDeviceConfirmation = useCallback(() => {
    setRebootDeviceConfirmationOpen(false);
    setRebootDeviceButtonEl(null);
  }, []);

  const rebootDevice = useCallback(async () => {
    setRebootStatus('pending');

    try {
      await miraClient.rebootDevice(device.id);
      setRebootStatus('success');
    } catch (err) {
      setRebootStatus('error');
    } finally {
      closeRebootDeviceConfirmation();
    }
  }, [device, closeRebootDeviceConfirmation]);

  const clearCache = useCallback(async () => {
    setClearCacheStatus('pending');

    try {
      await miraClient.clearDeviceCache(device.id);
      setClearCacheStatus('success');
    } catch (err) {
      setClearCacheStatus('error');
    }
  }, [device]);

  const resolveUnregisterConfirmation = useRef<
    ((result: boolean) => void) | null
  >(null);

  const showUnregisterConfirmation = useCallback(async (): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveUnregisterConfirmation.current = (result: boolean) => {
        resolveUnregisterConfirmation.current = null;
        setIsUnregisterConfirmationOpen(false);
        resolve(result);
      };

      setIsUnregisterConfirmationOpen(true);
    });
  }, [resolveUnregisterConfirmation, setIsUnregisterConfirmationOpen]);

  const unregisterDevice = useCallback(async () => {
    try {
      const userConfirmedRemove = await showUnregisterConfirmation();
      if (!userConfirmedRemove) {
        return;
      }
      setUnregisterStatus('pending');
      await miraClient.unregisterDevice(device.id);
      setUnregisterStatus('success');
      dispatch(deviceActions.removeDevice({ deviceId: device.id }));
      onClose();
    } catch (err) {
      setUnregisterStatus('error');
    }
  }, [device, dispatch, showUnregisterConfirmation, onClose]);

  // Side-effects

  // Fetch sound zone on load and periodically.
  useEffect(() => {
    const fetchSoundZone = () => {
      if (document.hidden) return;
      dispatch(soundZoneActions.fetchSoundZone(device.id));
    };

    // Fetch when window becomes active.
    window.addEventListener('visibilitychange', fetchSoundZone, false);

    // Fetch periodically.
    const interval = window.setInterval(fetchSoundZone, SYBSoundZonePollMS);

    // Fetch on page load.
    fetchSoundZone();

    return () => {
      window.removeEventListener('visibilitychange', fetchSoundZone, false);
      clearInterval(interval);
    };
  }, [dispatch, device.id]);

  useEffect(() => {
    setDevice(initialDevice);
  }, [initialDevice]);

  // Fetch pairing code on load.
  useEffect(() => {
    if (devicePairingCode) return;
    dispatch(pairingCodeActions.generatePairingCode(device.id));
  }, [dispatch, device, devicePairingCode]);

  // Update device resolution when changed.
  useEffect(() => {
    if (!device) return;
    if (updatedResolution === null) return;
    if (updatedResolution === device.screenDimensions) return;
    updateDevice({ screenDimensions: updatedResolution });
  }, [updateDevice, updatedResolution, device]);

  // Update device timezone when changed.
  useEffect(() => {
    if (!device) return;
    if (updatedTimezone === null) return;
    if (updatedTimezone === device.timezone) return;
    updateDevice({ timezone: updatedTimezone });
  }, [updateDevice, updatedTimezone, device]);

  // Update device orientation when changed.
  useEffect(() => {
    if (!device) return;
    if (updatedOrientation === null) return;
    if (updatedOrientation === device.screenOrientation) return;
    updateDevice({
      screenOrientation: updatedOrientation as D.ScreenOrientation,
    });
  }, [updateDevice, updatedOrientation, device]);

  // Update maintenance window toggle status when changed.
  useEffect(() => {
    if (!device) return;
    if (!device.activeHoursStart || !device.activeHoursStart) return;
    if (updatedMaintenanceWindowEnabled === null) return;

    if (!updatedMaintenanceWindowEnabled) {
      updateDevice({
        activeHoursStart: null,
        activeHoursEnd: null,
      });
    }
  }, [updateDevice, updatedMaintenanceWindowEnabled, device]);

  // Update active hour start/end time when changed
  useEffect(() => {
    if (!device) return;
    setActiveHoursStart(toTimeFieldValue(device.activeHoursStart));
    setActiveHoursEnd(toTimeFieldValue(device.activeHoursEnd));
  }, [device]);

  // Update daily reboot/reset times when changed
  useEffect(() => {
    if (!device) return;
    setDailyRestartTime(toTimeFieldValue(device.dailyRestartTime));
    setDailyRebootTime(toTimeFieldValue(device.dailyRebootTime));
  }, [device]);

  const onMusicServiceChange = useCallback(async () => {
    try {
      if (musicServiceStatus) {
        await miraClient.disableDeviceService(device.id, 'music');
        setMusicServiceStatus(false);
      } else {
        await miraClient.enableDeviceService(device.id, 'music');
        setMusicServiceStatus(true);
      }
    } catch (err) {
      return;
    }
  }, [device, musicServiceStatus]);

  // Render

  const {
    isOnline,
    signalStrength,
    wifiSsid,
    aiCameraStatus,
    rxCameraStatus,
    rxIrFrameStatus,
    activeMonitors,
  } = getDeviceConnectionStatus(device, lastLoadedDate);

  const isEthernet = device.metrics?.connectionType === 'ethernet';
  const isLTE = device.metrics?.connectionType === 'lte';
  const isWifi = !isEthernet && !isLTE;
  const aiCamStatus = aiCameraStatus || null;
  const rxCamStatus = rxCameraStatus || null;
  const irFrameStatus = rxIrFrameStatus || null;
  const connectedMonitors = activeMonitors || null;

  const isShareable =
    isEnterpriseUser &&
    currentUser &&
    canShareResource(currentUser, device.resource);

  const isDeleteable =
    currentUser && canDeleteLocationDevice(currentUser, device);

  const isPreBelenaDevice = !device.resinUuid && !device.isLite;

  const deviceType = getDeviceType(device);

  const maintenanceWindowSet = !!(
    device.activeHoursStart && device.activeHoursEnd
  );

  const maintenanceWindowEnabled =
    updatedMaintenanceWindowEnabled ?? maintenanceWindowSet ?? false;

  const renderIntegration = () =>
    !device.isLite &&
    !isPreBelenaDevice && <DeviceIntegrationManager device={device} />;

  const { t } = useTranslation(['devices']);

  const isDeviceSoC = device.deviceType === 'SoC';
  const isRaydiantConnect = device.deviceType === 'RaydiantLite';
  const rows = deviceManagementRows(device);

  return (
    <>
      <PaperModalV2.Body className="flex mb-4">
        <div>
          <div>
            <Text variant="subheader" className="text-black mb-1 flex">
              {device.name}
            </Text>
            {deviceType && (
              <Heading size={5} className={classes.type}>
                {deviceType}
              </Heading>
            )}
          </div>
          <DeviceStatus
            device={device}
            contentWarning={contentWarning}
            deviceErrors={deviceErrors}
            lastLoadedDate={lastLoadedDate}
          />
        </div>
        <br />
      </PaperModalV2.Body>
      <Scrollable>
        <PaperModalV2.Body>
          <div className={classes.section}>
            <Column>
              <div>
                <Text variant="smallSubheader" className="text-black mb-3 flex">
                  Properties
                </Text>
                <PropertiesTable>
                  <tbody>
                    {propertyRows(device, { isEnterpriseUser }).map(
                      ({ label, value }) => (
                        <tr key={label}>
                          <th>{label}</th>
                          <td>{value}</td>
                        </tr>
                      ),
                    )}
                    {isDeviceSoC && (
                      <>
                        <tr>
                          <th>Manufacturer</th>
                          <td>{device.screenManufacturer}</td>
                        </tr>
                        <tr>
                          <th>Model</th>
                          <td>{device.screenModel}</td>
                        </tr>
                        <tr>
                          <th>OS Version</th>
                          <td>{device.screenOsVersion}</td>
                        </tr>
                        <tr>
                          <th>Serial Number</th>
                          <td>{device.screenSerialNumber}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </PropertiesTable>
              </div>

              <TextArea
                rows={4}
                label="Notes"
                value={updatedNotes ?? device.description ?? ''}
                onChange={setNotes}
                onBlur={updateNotes}
              />

              {!device.isLite && !isVIOnly && !isDeviceSoC && (
                <Select
                  label="Screen Resolution"
                  selectedKey={
                    updatedResolution ?? device.screenDimensions ?? ''
                  }
                  onSelectionChange={(key) => setResolution(key as string)}
                >
                  {roles.is4kEnabled && is8kResolutionSupported(device.model) && (
                    <SelectItem key="7680×4320" id="7680×4320">
                      8K [UHD]
                    </SelectItem>
                  )}
                  {roles.is4kEnabled && is4kResolutionSupported(device.model) && (
                    <SelectItem key="3840x2160" id="3840x2160">
                      4K [UHD]
                    </SelectItem>
                  )}
                  <SelectItem key="1920x1080" id="1920x1080">
                    1080p
                  </SelectItem>
                  <SelectItem key="1280x720" id="1280x720">
                    720p
                  </SelectItem>
                </Select>
              )}

              {!device.isLite && !isDeviceSoC && (
                <TimezoneSelect
                  label="Timezone"
                  onChange={setTimezone}
                  value={updatedTimezone ?? device.timezone ?? ''}
                />
              )}

              {!isVIOnly && !isDeviceSoC && (
                <Select
                  label={t('screens.screenOrientation')!}
                  selectedKey={
                    updatedOrientation ?? device.screenOrientation ?? ''
                  }
                  onSelectionChange={(key) => setOrientation(key as string)}
                >
                  <SelectItem id="normal" key="normal">
                    {t('screens.landscape')!}
                  </SelectItem>
                  <SelectItem id="right" key="right">
                    {t('screens.portraitRight')!}
                  </SelectItem>
                  <SelectItem id="left" key="left">
                    {t('screens.portraitLeft')!}
                  </SelectItem>
                  <SelectItem id="inverted" key="inverted">
                    {t('screens.inverted')!}
                  </SelectItem>
                </Select>
              )}
            </Column>
          </div>

          {roles.isUserAIEnabled && renderIntegration()}

          {showsConnectivity(device) && !isPreBelenaDevice && (
            <div className={classes.section}>
              <Text variant="smallSubheader" className="text-black mb-3 flex">
                Connectivity
              </Text>
              <PropertiesTable>
                <tbody>
                  <tr>
                    <th>Connection type</th>
                    <td>
                      {isEthernet && (
                        <div className={classes.cellWithIcon}>
                          <span>Ethernet</span>
                          <EthernetIcon />
                        </div>
                      )}
                      {isLTE && (
                        <div className={classes.cellWithIcon}>
                          <span>LTE</span>
                          <LTEIcon />
                        </div>
                      )}
                      {isWifi && (
                        <div className={classes.cellWithIcon}>
                          <span>Wireless</span>
                          <WifiIcon className="size-[18px]" />
                        </div>
                      )}
                    </td>
                  </tr>
                  {!isEthernet && (
                    <tr>
                      <th>Signal strength</th>
                      <td>
                        {isOnline && (
                          <div className={classes.cellWithIcon}>
                            <span>{signalStrength}</span>
                            <CheckCircleIcon className="size-[18px]" />
                          </div>
                        )}
                        {!isOnline && (
                          <div className={classes.cellWithIcon}>
                            <span>No signal</span>
                            <ErrorIcon />
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  {isWifi && (
                    <tr>
                      <th>Network</th>
                      <td>
                        <div className={classes.cellWithIcon}>
                          {wifiSsid ? (
                            <span>{wifiSsid}</span>
                          ) : (
                            <span>N/A</span>
                          )}
                          <NetworkIcon />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </PropertiesTable>
            </div>
          )}

          {isOnline && (roles.isVIEnabled || roles.isRXEnabled) && (
            <div className={classes.section}>
              <Heading size={5} overline gutterBottom>
                Connected Sensors
              </Heading>
              <PropertiesTable>
                <tbody>
                  {roles.isVIEnabled && (
                    <tr>
                      <th>VI Camera</th>
                      <td>
                        <div className={classes.cellWithIcon}>
                          <span>
                            {aiCamStatus?.connected
                              ? 'Connected'
                              : 'Not connected'}
                          </span>
                          {aiCamStatus?.connected ? (
                            <CheckCircleIcon />
                          ) : (
                            <HighlightOffIcon />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  {roles.isRXEnabled && (
                    <>
                      <tr>
                        <th>RX Camera</th>
                        <td>
                          <div className={classes.cellWithIcon}>
                            <span>
                              {rxCamStatus?.connected
                                ? 'Connected'
                                : 'Not connected'}
                            </span>
                            {rxCamStatus?.connected ? (
                              <CheckCircleIcon />
                            ) : (
                              <HighlightOffIcon />
                            )}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <th>IR Frame</th>
                        <td>
                          <div className={classes.cellWithIcon}>
                            <span>
                              {irFrameStatus?.connected
                                ? 'Connected'
                                : 'Not connected'}
                            </span>
                            {irFrameStatus?.connected ? (
                              <CheckCircleIcon />
                            ) : (
                              <HighlightOffIcon />
                            )}
                          </div>
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <th></th>
                    <td></td>
                  </tr>
                </tbody>
              </PropertiesTable>
            </div>
          )}

          {isOnline &&
            !isDeviceSecondScreen(device) &&
            !isDeviceSoC &&
            !isRaydiantConnect &&
            !isPreBelenaDevice &&
            !isDeviceSurfaceGo(device) &&
            !isDevicePoly(device) && (
              <div className={classes.section}>
                <Text variant="smallSubheader" className="text-black mb-3 flex">
                  Display Outputs
                </Text>
                <PropertiesTable>
                  <tbody>
                    {connectedMonitors?.monitors?.map(
                      (monitor: D.Monitor, index: number) => (
                        <tr>
                          <th>{monitor.port ? monitor.port : 'None'}</th>
                          <td>
                            <div className={classes.cellWithIcon}>
                              <span>
                                {monitor.message
                                  ? monitor.message
                                  : 'Connected'}
                              </span>
                              {monitor.message ? (
                                <ErrorIcon />
                              ) : (
                                <CheckCircleIcon />
                              )}
                            </div>
                          </td>
                        </tr>
                      ),
                    )}
                    {connectedMonitors?.error && (
                      <tr>
                        <th>Error</th>
                        <td>
                          <div className={classes.cellWithIcon}>
                            <span>{connectedMonitors?.error}</span>
                            <ErrorIcon />
                          </div>
                        </td>
                      </tr>
                    )}
                    {!connectedMonitors?.error &&
                      connectedMonitors?.monitors == null && (
                        <tr>
                          <th>Display</th>
                          <td>
                            <div className={classes.cellWithIcon}>
                              <span>Unable to detect display</span>
                              <QuestionMarkIcon />
                            </div>
                          </td>
                        </tr>
                      )}
                  </tbody>
                </PropertiesTable>
              </div>
            )}

          {!isLiteUser && !isPreBelenaDevice && (
            <div className={classes.section}>
              <DeviceTags device={device} />
            </div>
          )}

          {isShareable && (
            <div className={classes.section}>
              <DeviceSharing device={device} />
            </div>
          )}

          <div className={classes.section}>
            <Column>
              <Text variant="smallSubheader" className="text-black mb-3 flex">
                Device Management
              </Text>

              {rows.blackout && (
                <>
                  <Text as="label" htmlFor="maintenanceWindowEnabled">
                    Device Update Blackout
                  </Text>
                  <Toggle
                    id="maintenanceWindowEnabled"
                    isSelected={maintenanceWindowEnabled}
                    onChange={setMaintenanceWindowEnabled}
                  >
                    Specifies a time of day in which no software updates should
                    occur.
                  </Toggle>
                </>
              )}

              {/* A Stick offers no blackout, since Amazon chooses when it
                  updates, so one set through the batch dialog is not shown. */}
              {maintenanceWindowEnabled && !isAmazonSignageStick(device) && (
                <div>
                  <Row halfMargin className="mb-1">
                    <TimeField
                      label="from"
                      value={activeHoursStart}
                      onChange={setActiveHoursStart}
                      error={!maintenanceWindowValid}
                      onBlur={updateActiveHoursStartTime}
                    />
                    <TimeField
                      label="to"
                      value={activeHoursEnd}
                      onChange={setActiveHoursEnd}
                      error={!maintenanceWindowValid}
                      onBlur={updateActiveHoursEndTime}
                    />
                  </Row>
                  <Text className="text-acctconsole ml-4">
                    Maximum range of {maximumDeviceUpdateBlackoutRange} hours.{' '}
                    <Link
                      href={`${supportAppUrl}/getting-started/5imknKqrcfswGZ9RmtHfPK/dashboard-introduction-and-basics/5imknKqrcinJNiXdEoT8cC`}
                      target="_blank"
                      inheritFontSize
                    >
                      Learn more
                    </Link>
                  </Text>
                </div>
              )}

              {!isPreBelenaDevice && (
                <>
                  <div>
                    <Row halfMargin className={cn(classes.section1, 'mb-1')}>
                      <div>
                        <Button
                          fullWidth
                          isDisabled={restartStatus === 'pending'}
                          onPress={restartDevice}
                          variant="primary"
                          className="h-[40px]"
                        >
                          {restartStatus === 'pending'
                            ? 'Restarting...'
                            : 'Restart'}
                        </Button>
                        {restartStatus === 'error' && (
                          <InputHelperText error>
                            Failed to restart, please try again later.
                          </InputHelperText>
                        )}
                      </div>
                      {rows.dailyTimes && (
                        <TimeField
                          label="Repeats daily"
                          value={dailyRestartTime}
                          onChange={setDailyRestartTime}
                          onBlur={updateDailyRestartTime}
                        />
                      )}
                    </Row>
                    <Text className="text-acctconsole ml-4">
                      Will restart the software.{' '}
                      <Link
                        href={`${supportAppUrl}/getting-started/5imknKqrcfswGZ9RmtHfPK/dashboard-introduction-and-basics/5imknKqrcinJNiXdEoT8cC`}
                        target="_blank"
                        inheritFontSize
                      >
                        Learn more
                      </Link>
                    </Text>
                  </div>

                  {rows.reboot && (
                    <div>
                      <Row halfMargin className={cn(classes.section1, 'mb-1')}>
                        <div>
                          <Button
                            fullWidth
                            isDisabled={rebootStatus === 'pending'}
                            onPress={openRebootDeviceConfirmation}
                            className="h-[40px]"
                          >
                            {rebootStatus === 'pending'
                              ? 'Rebooting...'
                              : 'Reboot'}
                          </Button>

                          {rebootStatus === 'error' && (
                            <InputHelperText error>
                              Failed to reboot, please try again later.
                            </InputHelperText>
                          )}
                        </div>
                        <TimeField
                          label="Repeats daily"
                          value={dailyRebootTime}
                          onChange={setDailyRebootTime}
                          onBlur={updateDailyRebootTime}
                        />
                      </Row>
                      <Text className="ml-4 text-acctconsole">
                        Will reboot the software.{' '}
                        <Link
                          href={`${supportAppUrl}/getting-started/5imknKqrcfswGZ9RmtHfPK/dashboard-introduction-and-basics/5imknKqrcinJNiXdEoT8cC`}
                          target="_blank"
                          inheritFontSize
                        >
                          Learn more
                        </Link>
                      </Text>
                      <Popover
                        variant="alert"
                        open={rebootDeviceConfirmationOpen}
                        anchorEl={rebootDeviceButtonEl}
                        onClose={closeRebootDeviceConfirmation}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <Column>
                          <div>
                            <Heading2>Reboot Will...</Heading2>
                            <ul>
                              <li>
                                Cause this device to disconnect and attempt to
                                reconnect to the network.
                              </li>
                              <li>
                                It will not be accessible from the Dashboard
                                during this time.
                              </li>
                              <li>
                                Please use caution if you do not have physical
                                access to this device.
                              </li>
                            </ul>
                          </div>
                          <Row>
                            <Button
                              fullWidth
                              onPress={closeRebootDeviceConfirmation}
                            >
                              Dismiss
                            </Button>
                            <Button
                              fullWidth
                              isDisabled={rebootStatus === 'pending'}
                              variant="primary"
                              onPress={rebootDevice}
                            >
                              {rebootStatus === 'pending'
                                ? 'Rebooting...'
                                : 'Reboot'}
                            </Button>
                          </Row>
                        </Column>
                      </Popover>
                    </div>
                  )}

                  {rows.contentCache && (
                    <div>
                      <Row halfMargin className={cn(classes.section1, 'mb-1')}>
                        <div>
                          <Button
                            fullWidth
                            isDisabled={clearCacheStatus === 'pending'}
                            onPress={clearCache}
                            variant="primary"
                            className="h-[40px]"
                          >
                            {clearCacheStatus === 'pending'
                              ? 'Clearing...'
                              : 'Clear cache'}
                          </Button>
                          {clearCacheStatus === 'error' && (
                            <InputHelperText error>
                              Failed to clear the cache, please try again later.
                            </InputHelperText>
                          )}
                        </div>
                        {/* No time field: a cache clear is only ever sent by hand. */}
                        <div />
                      </Row>
                      <Text className="text-acctconsole ml-4">
                        Will clear cached content and reload the screen.
                      </Text>
                    </div>
                  )}
                </>
              )}
              {isDeleteable && (
                <div>
                  <Button
                    fullWidth
                    size="large"
                    variant="caution"
                    isDisabled={unregisterStatus === 'pending'}
                    onPress={unregisterDevice}
                  >
                    {unregisterStatus === 'pending'
                      ? 'Removing...'
                      : `${
                          roles.isVIOnlyEnabled
                            ? 'Remove Device'
                            : 'Remove Screen'
                        }`}
                  </Button>
                  {unregisterStatus === 'error' && (
                    <InputHelperText error>
                      Failed to unregister, please try again later.
                    </InputHelperText>
                  )}
                </div>
              )}
            </Column>
          </div>

          {!device.isLite && !isPreBelenaDevice && (
            <div className={classes.section}>
              <div>
                <div className="mb-3">
                  <Text
                    variant="smallSubheader"
                    className="text-black mb-1 flex"
                  >
                    Soundtrack Your Brand
                  </Text>
                  <Link
                    href={`${supportAppUrl}/digital-signage/5imknKqrcgd6fwhKk5jfp3/soundtrack-your-brand--displai/5m37mbTvRQqnrBdPnT4bHX`}
                    target="_blank"
                  >
                    What's this?
                  </Link>
                </div>

                <PropertiesTable>
                  <tbody>
                    <tr>
                      <th>Pairing code</th>
                      <td>
                        {isGeneratingPairingCode && <span>Checking...</span>}
                        {devicePairingCode && !isGeneratingPairingCode && (
                          <span>{devicePairingCode.pairingCode}</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>
                        {isFetchingSoundZone && <span>Checking...</span>}
                        {deviceSoundZone && !isFetchingSoundZone && (
                          <span>Paired</span>
                        )}
                        {!deviceSoundZone && !isFetchingSoundZone && (
                          <span>Not paired</span>
                        )}
                      </td>
                    </tr>
                    {deviceSoundZone && (
                      <tr>
                        <th>Zone</th>
                        <td>{deviceSoundZone.name}</td>
                      </tr>
                    )}
                    <tr>
                      <th>Enable</th>
                      <td>
                        <Toggle
                          isSelected={musicServiceStatus}
                          onChange={onMusicServiceChange}
                        />
                      </td>
                    </tr>
                  </tbody>
                </PropertiesTable>
              </div>
            </div>
          )}
        </PaperModalV2.Body>
      </Scrollable>

      <UnregisterDeviceConfirmation
        device={device}
        open={isUnregisterConfirmationOpen}
        onConfirm={resolveUnregisterConfirmation.current}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    title: {
      marginBottom: theme.spacing(0.5),
    },

    type: {
      margin: 0,
    },

    cellWithIcon: {
      display: 'flex',
      justifyContent: 'space-between',
    },

    section: {
      marginBottom: theme.spacing(5),

      '&:last-child': {
        marginBottom: 0,
      },
    },

    sybLink: {
      fontWeight: 400,
      textTransform: 'none',
      letterSpacing: 0,
    },

    section1: {
      display: 'flex',
      alignItems: 'flex-end',
      '& :first-child': {
        flex: 7,
      },
      '& :nth-child(2)': {
        flex: 5,
      },
    },
  });
});

export default DeviceSettings;
